import { useCallback, useState } from "react";
import { toast } from "sonner";

/**
 * Version-history interactions for the Output page: the restore-confirm flow and
 * the diff dialog's open state.
 *
 * The important behaviour to preserve is that restoring is a *preview*, not a
 * write: it loads the snapshot into the live editor as unsaved changes and sets a
 * pending label, so the user must press Save to commit it as a new version.
 * Cancelling therefore has to re-read from the server rather than trying to undo
 * locally — the reducer has no history stack to rewind (see archReducer).
 *
 * @param {object}   args
 * @param {Function} args.dispatch              archReducer dispatch
 * @param {Function} args.setPendingRestoreLabel from useHierarchyPatch
 * @param {Function} args.reloadHierarchy       re-fetch the server's current tree
 */
export function useVersionHistory({ dispatch, setPendingRestoreLabel, reloadHierarchy }) {
  const [restoreConfirm, setRestoreConfirm] = useState(null); // version awaiting confirm
  const [diffOpen, setDiffOpen] = useState(false);

  const onRestoreVersion = useCallback((version) => setRestoreConfirm(version), []);

  const confirmRestore = useCallback(() => {
    if (!restoreConfirm) return;
    const snap = restoreConfirm.snapshot || {};
    dispatch({
      type: "RESTORE_SNAPSHOT",
      nodes: snap.nodes || [],
      peerConnections: snap.peerConnections || [],
    });
    setPendingRestoreLabel(`restored from v${restoreConfirm.version}`);
    toast.success(`Previewing v${restoreConfirm.version}`, {
      description: "Click Save to commit this as a new version.",
    });
    setRestoreConfirm(null);
  }, [restoreConfirm, dispatch, setPendingRestoreLabel]);

  const cancelRestorePreview = useCallback(() => {
    // Re-load the latest server hierarchy (the same as initial load).
    setPendingRestoreLabel(null);
    reloadHierarchy();
  }, [setPendingRestoreLabel, reloadHierarchy]);

  return {
    restoreConfirm,
    setRestoreConfirm,
    diffOpen,
    setDiffOpen,
    onRestoreVersion,
    confirmRestore,
    cancelRestorePreview,
  };
}
